import type { ClassInformationInterface, CollectionViewModelInterface, CoreOrchestratorViewModelInterface, ExternalViewModelInterface, IdentityInterface, InternalViewModelInterface, ItemViewModelInterface, MasterDetailOrchestratorViewModelInterface, MasterDetailRootViewModelInterface, MasterViewModelInterface, ModelInterface, PropertyViewModelInterface, RootViewModelInterface } from '@nts/std/interfaces';
import { ClassAdditionalInfo, ClassInformationType } from "@nts/std/types";

export class ClassInformationUtility {

  /**
   * Verifies if the given instance is of the specified class information type.
   * @deprecated use isClassType instead
   *
   * @param instance
   * @param classInformationType
   * @returns
   */
  static checkClassType(instance: ClassInformationInterface, classInformationType: ClassInformationType): boolean {
    return instance.classType === classInformationType;
  }

  /**
   * Verifies if the given instance has the specified class additional information.
   * @deprecated use hasClassAdditionalInfo instead
   *
   * @param instance
   * @param classAdditionalInfo
   * @returns
   */
  static checkClassAdditionalInfo(instance: ClassInformationInterface, classAdditionalInfo: ClassAdditionalInfo): boolean {
    return instance.classAdditionalInfo === classAdditionalInfo;
  }

  /**
   * Verifies if the given instance is of the specified class information type.
   *
   * @param instance
   * @param classInformationType
   * @returns
   */
  static isClassType(
    instance: ClassInformationInterface | null | undefined,
    classInformationType: ClassInformationType
  ): boolean {
    return !!instance && instance.classType === classInformationType;
  }

  /**
   * Verifies if the given instance has the specified class additional information.
   *
   * @param instance
   * @param classAdditionalInfo
   * @returns
   */
  static hasClassAdditionalInfo(
    instance: ClassInformationInterface | null | undefined,
    classAdditionalInfo: ClassAdditionalInfo
  ): boolean {
    return !!instance && instance.classAdditionalInfo === classAdditionalInfo;
  }

  /**
   * Verifies if the given instance is an external view model.
   *
   * @param instance
   * @returns
   */
  static isExternalViewModel<
    TIdentity extends IdentityInterface = IdentityInterface,
    TModel extends ModelInterface<TIdentity> = ModelInterface<TIdentity>
  >(
    instance: ClassInformationInterface | null | undefined
  ): instance is ExternalViewModelInterface<TIdentity, TModel> {
    return !!instance && instance.classType === ClassInformationType.ExternalViewModel;
  }

  /**
   * Verifies if the given instance is an internal view model.
   *
   * @param instance
   * @returns
   */
  static isInternalViewModel<
    TIdentity extends IdentityInterface = IdentityInterface,
    TModel extends ModelInterface<TIdentity> = ModelInterface<TIdentity>
  >(
    instance: ClassInformationInterface | null | undefined
  ): instance is InternalViewModelInterface<TIdentity, TModel> {
    return !!instance && instance.classType === ClassInformationType.InternalViewModel;
  }

  /**
   * Verifies if the given instance is a root view model.
   *
   * @param instance
   * @returns
   */
  static isRootViewModel<
    TIdentity extends IdentityInterface = IdentityInterface,
    TModel extends ModelInterface<TIdentity> = ModelInterface<TIdentity>,
  >(
    instance: ClassInformationInterface | null | undefined
  ): instance is RootViewModelInterface<TIdentity, TModel> {
    return !!instance && instance.classType === ClassInformationType.RootViewModel;
  }

  /**
   * Verifies if the given instance is a collection view model.
   *
   * @param instance
   * @returns
   */
  static isCollectionViewModel<
    TItemIdentity extends IdentityInterface = IdentityInterface,
    TItemModel extends ModelInterface<TItemIdentity> = ModelInterface<TItemIdentity>,
    TItemViewModel extends ItemViewModelInterface<TItemIdentity, TItemModel> = ItemViewModelInterface<TItemIdentity, TItemModel>,
    TOrchestrator = CoreOrchestratorViewModelInterface
  >(
    instance: ClassInformationInterface | null | undefined
  ): instance is CollectionViewModelInterface<
    TItemIdentity,
    TItemModel,
    TItemViewModel,
    TOrchestrator
  > {
    return !!instance && instance.classType === ClassInformationType.CollectionViewModel;
  }

  /**
   * Verifies if the given instance is an item view model.
   *
   * @param instance
   * @returns
   */
  static isItemViewModel<
    TIdentity extends IdentityInterface = IdentityInterface,
    TModel extends ModelInterface<TIdentity> = ModelInterface<TIdentity>
  >(
    instance: ClassInformationInterface | null | undefined
  ): instance is ItemViewModelInterface<TIdentity, TModel> {
    return !!instance && instance.classType === ClassInformationType.ItemViewModel;
  }

  /**
   * Vwerifies if the given instance is a property view model.
   *
   * @param instance
   * @returns
   */
  static isPropertyViewModel<
    T = any
  >(
    instance: ClassInformationInterface | null | undefined
  ): instance is PropertyViewModelInterface<T> {
    return !!instance && instance.classType === ClassInformationType.PropertyViewModel;
  }

  /**
   * Verifies if the given instance is a master view model.
   *
   * @param instance
   * @returns
   */
  static isMasterViewModel<
    TIdentity extends IdentityInterface = IdentityInterface,
    TModel extends ModelInterface<TIdentity> = ModelInterface<TIdentity>,
    TRootViewModel extends MasterDetailRootViewModelInterface<TIdentity, TModel> = MasterDetailRootViewModelInterface<TIdentity, TModel>
  >(
    instance: ClassInformationInterface | null | undefined
  ): instance is MasterViewModelInterface<TIdentity, TModel, TRootViewModel> {
    return !!instance && instance.classType === ClassInformationType.MasterViewModel;
  }

  static isMasterDetailOrchestratorViewModel<
    TRootViewModel extends RootViewModelInterface = RootViewModelInterface,
    TMasterViewModel extends MasterViewModelInterface = MasterViewModelInterface
  >(
    instance: ClassInformationInterface | null | undefined
  ): instance is MasterDetailOrchestratorViewModelInterface<
    TRootViewModel,
    TMasterViewModel
  > {
    return !!instance && instance.classType === ClassInformationType.MasterDetailOrchestratorViewModel;
  }
}
